import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Landmark,
    TrendingUp,
    Users,
    Home,
    RefreshCw,
    X,
    ExternalLink,
    Percent,
    Coins,
    ChevronDown,
    ChevronRight,
    ArrowUp,
    ArrowDown,
    ArrowUpDown,
} from 'lucide-react';
import { fetchMortgageCalculatorAnalytics, fetchEventDetails } from '../api/analyticsApi';
import { syncVirtualContact } from '../../../api/contact';
import toast from 'react-hot-toast';

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(val)) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(val);
};

const formatPercent = (val) => {
    if (val === null || val === undefined || Number.isNaN(val)) return '—';
    return `${Number(val).toFixed(2)}%`;
};

const formatRelative = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 60) return `${Math.max(1, m)}m ago`;
    const h = Math.floor(diffMs / 3600000);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(diffMs / 86400000);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

const SOURCE_LABELS = {
    direct: 'Direct',
    property_detail: 'Property page',
    property_detail_mini: 'Property mini widget',
    investment_calculator: 'Investment calculator',
    full_page: 'Full calculator',
    unknown: 'Unknown',
};

const BUYER_TYPE_LABELS = {
    investor: 'Investor',
    first_home_buyer: 'First home buyer',
    owner_occupier: 'Owner-occupier',
};

const mapEventToRow = (ev) => {
    const props = ev.properties_json || {};
    return {
        event_id: ev.event_id,
        occurred_at: ev.occurred_at,
        buyer: ev.buyer || null,
        loan_amount: props.loan_amount,
        property_price: props.property_price,
        repayment: props.repayment,
        lvr_pct: props.lvr_pct,
        borrowing_power: props.borrowing_power,
        loan_type: props.loan_type,
        state_code: props.state_code,
        buyer_type: props.buyer_type,
        source: props.source,
        property: ev.property,
    };
};

const SortHeader = ({ label, column, sortBy, sortDir, onSort }) => {
    const active = sortBy === column;
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            className={`inline-flex items-center gap-1 uppercase hover:text-gray-900 transition-colors flex-row-reverse ${active ? 'text-teal-600 font-semibold' : ''}`}
            title={`Sort by ${label.toLowerCase()}`}
        >
            {label}
            {active
                ? (sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                : <ArrowUpDown className="w-3 h-3 opacity-40" />}
        </button>
    );
};

const StatCard = ({ icon: Icon, label, value, sub, tint = 'blue', onClick }) => {
    const tints = {
        blue: 'bg-blue-50 text-blue-600',
        green: 'bg-emerald-50 text-emerald-600',
        purple: 'bg-purple-50 text-purple-600',
        amber: 'bg-amber-50 text-amber-600',
        teal: 'bg-teal-50 text-teal-600',
    };
    const clickable = typeof onClick === 'function';
    return (
        <div
            className={`bg-white rounded-lg shadow p-4 flex items-start gap-3 transition ${
                clickable ? 'cursor-pointer hover:shadow-md hover:bg-gray-50' : ''
            }`}
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
        >
            <div className={`p-2 rounded-lg flex-shrink-0 ${tints[tint] || tints.blue}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                {sub && <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>}
            </div>
        </div>
    );
};

const MortgageCalculatorAnalytics = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [redirectingBuyer, setRedirectingBuyer] = useState(false);
    const [drilldown, setDrilldown] = useState(null);
    const [drilldownRows, setDrilldownRows] = useState([]);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [buyersOpen, setBuyersOpen] = useState(false);
    const [sortBy, setSortBy] = useState('opens'); // 'opens' | 'calculations' | 'broker_clicks' | 'last_at'
    const [sortDir, setSortDir] = useState('desc'); // 'desc' | 'asc'

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortBy(column);
            setSortDir('desc');
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchMortgageCalculatorAnalytics(startDate, endDate)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    useEffect(() => {
        if (!drilldown) {
            setDrilldownRows([]);
            return undefined;
        }

        let cancelled = false;
        setDrilldownLoading(true);
        setDrilldownRows([]);

        const eventMap = {
            opens: 'mortgage_calculator_opened',
            calculations: 'mortgage_calculator_calculated',
            broker: 'mortgage_calculator_broker_clicked',
        };
        const eventName = eventMap[drilldown.filter] || 'mortgage_calculator_calculated';

        fetchEventDetails(startDate, endDate, eventName, null, null, 500)
            .then((events) => {
                if (cancelled) return;
                let rows = (events || []).map(mapEventToRow);
                if (drilldown.filter === 'signed_in') {
                    rows = rows.filter((r) => r.buyer);
                }
                setDrilldownRows(rows);
            })
            .catch(() => {
                if (!cancelled) toast.error('Could not load activity details.');
            })
            .finally(() => {
                if (!cancelled) setDrilldownLoading(false);
            });

        return () => { cancelled = true; };
    }, [drilldown, startDate, endDate]);

    const openBuyer = async (buyer) => {
        if (!buyer || redirectingBuyer) return;
        setRedirectingBuyer(true);
        const toastId = toast.loading(`Opening ${buyer.name || buyer.email}…`);
        try {
            const response = await syncVirtualContact(`buyer_${buyer.id}`);
            const contactId = response?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`, {
                state: { returnTo: '/analytics?view=calculators', returnLabel: 'Calculators' },
            });
        } catch (err) {
            toast.error('Could not open buyer profile.', { id: toastId });
        } finally {
            setRedirectingBuyer(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center h-40">
                <RefreshCw className="w-5 h-5 text-teal-500 animate-spin mr-2" />
                <span className="text-gray-500 text-sm">Loading mortgage calculator analytics…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Mortgage Calculator</h2>
                <p className="text-rose-600 text-sm">{error}</p>
            </div>
        );
    }

    if (!data) return null;

    const {
        total_opens = 0,
        total_calculations = 0,
        total_broker_clicks = 0,
        mini_calculations = 0,
        unique_users = 0,
        authenticated_users = 0,
        loan_type_breakdown = { PI: 0, IO: 0 },
        buyer_type_breakdown = {},
        source_breakdown = [],
        state_breakdown = [],
        frequency_breakdown = {},
        averages = {},
        top_properties = [],
        buyer_summaries = [],
        seeded_calculations_excluded = 0,
    } = data;

    const totalLoanTypes = (loan_type_breakdown.PI || 0) + (loan_type_breakdown.IO || 0);
    const piPct = totalLoanTypes > 0 ? Math.round((loan_type_breakdown.PI / totalLoanTypes) * 100) : 0;
    const ioPct = totalLoanTypes > 0 ? 100 - piPct : 0;

    const buyerTypes = [
        { key: 'investor', label: 'Investor', color: 'bg-purple-500' },
        { key: 'first_home_buyer', label: 'First home buyer', color: 'bg-emerald-500' },
        { key: 'owner_occupier', label: 'Owner-occupier', color: 'bg-blue-500' },
    ];
    const totalBuyerTypes = buyerTypes.reduce((s, t) => s + (buyer_type_breakdown[t.key] || 0), 0);

    const anonCount = Math.max(0, unique_users - authenticated_users);
    const fullPageCalcs = Math.max(0, total_calculations - mini_calculations);

    const maxStateCount = state_breakdown.length
        ? Math.max(...state_breakdown.map((s) => s.count))
        : 0;

    const sortedBuyers = [...buyer_summaries].sort((a, b) => {
        let av;
        let bv;
        if (sortBy === 'last_at') {
            av = a.last_at ? new Date(a.last_at).getTime() : 0;
            bv = b.last_at ? new Date(b.last_at).getTime() : 0;
        } else {
            av = a[sortBy] || 0;
            bv = b[sortBy] || 0;
        }
        return sortDir === 'desc' ? bv - av : av - bv;
    });

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-teal-600" />
                    Mortgage Calculator
                </h3>
                <p className="text-sm text-gray-500">
                    Repayments, upfront costs, borrowing power and broker CTA usage
                </p>
                {seeded_calculations_excluded > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                        Every figure below is a calculation a buyer actually entered.{' '}
                        {seeded_calculations_excluded.toLocaleString()} older rows the
                        calculator logged before the buyer touched anything are excluded.
                    </p>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard
                    icon={Landmark}
                    label="Opens"
                    value={total_opens}
                    sub={`${unique_users} unique users`}
                    tint="teal"
                    onClick={total_opens > 0 ? () => setDrilldown({ title: 'Mortgage calculator opens', filter: 'opens' }) : undefined}
                />
                <StatCard
                    icon={TrendingUp}
                    label="Calculations"
                    value={total_calculations}
                    sub={`${fullPageCalcs} full · ${mini_calculations} mini`}
                    tint="green"
                    onClick={total_calculations > 0 ? () => setDrilldown({ title: 'All mortgage calculations', filter: 'calculations' }) : undefined}
                />
                <StatCard
                    icon={Users}
                    label="Signed-in users"
                    value={authenticated_users}
                    sub={`${anonCount} anon`}
                    tint="purple"
                    onClick={authenticated_users > 0 ? () => setDrilldown({ title: 'Signed-in mortgage activity', filter: 'signed_in' }) : undefined}
                />
                <StatCard
                    icon={Percent}
                    label="Avg repayment"
                    value={formatCurrency(averages.avg_repayment)}
                    sub={averages.avg_lvr_pct ? `${formatPercent(averages.avg_lvr_pct)} avg LVR` : null}
                    tint="amber"
                    onClick={fullPageCalcs > 0 ? () => setDrilldown({ title: 'Repayment calculations', filter: 'calculations' }) : undefined}
                />
                <StatCard
                    icon={Coins}
                    label="Broker CTA"
                    value={total_broker_clicks}
                    sub="Compare City clicks"
                    tint="blue"
                    onClick={total_broker_clicks > 0 ? () => setDrilldown({ title: 'Broker application clicks', filter: 'broker' }) : undefined}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-5 space-y-5">
                    <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-3">Loan type</h4>
                        {totalLoanTypes === 0 ? (
                            <p className="text-sm text-gray-500">No calculations yet.</p>
                        ) : (
                            <>
                                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-3">
                                    <div className="bg-teal-500" style={{ width: `${piPct}%` }} />
                                    <div className="bg-amber-500" style={{ width: `${ioPct}%` }} />
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-700">Principal &amp; interest</span>
                                    <span className="font-semibold">{loan_type_breakdown.PI} ({piPct}%)</span>
                                </div>
                                <div className="flex justify-between text-sm mt-2">
                                    <span className="text-gray-700">Interest only</span>
                                    <span className="font-semibold">{loan_type_breakdown.IO} ({ioPct}%)</span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-bold text-gray-900 mb-3">Buyer profile</h4>
                        {totalBuyerTypes === 0 ? (
                            <p className="text-sm text-gray-500">No profile data yet.</p>
                        ) : (
                            buyerTypes.map((t) => {
                                const count = buyer_type_breakdown[t.key] || 0;
                                const pct = Math.round((count / totalBuyerTypes) * 100);
                                return (
                                    <div key={t.key} className="mb-2">
                                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                                            <span>{t.label}</span>
                                            <span>{count} ({pct}%)</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                            <div className={t.color} style={{ width: `${pct}%`, height: '100%' }} />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="pt-4 border-t border-gray-100 space-y-1.5 text-sm">
                        <div className="flex justify-between"><span className="text-gray-500">Avg loan</span><span className="font-medium">{formatCurrency(averages.avg_loan_amount)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Avg property price</span><span className="font-medium">{formatCurrency(averages.avg_property_price)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Avg rate</span><span className="font-medium">{formatPercent(averages.avg_rate_pct)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Avg borrowing power</span><span className="font-medium">{formatCurrency(averages.avg_borrowing_power)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Avg upfront costs</span><span className="font-medium">{formatCurrency(averages.avg_upfront_costs)}</span></div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-5">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">By state (AU)</h4>
                    {state_breakdown.length === 0 ? (
                        <p className="text-sm text-gray-500">No state data yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {state_breakdown.map((s) => (
                                <li key={s.state}>
                                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                                        <span className="font-medium">{s.state}</span>
                                        <span>{s.count}</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                        <div
                                            className="bg-teal-500 h-full"
                                            style={{ width: `${maxStateCount ? (s.count / maxStateCount) * 100 : 0}%` }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {Object.keys(frequency_breakdown).length > 0 && (
                        <div className="mt-5 pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-900 mb-2">Repayment frequency</h4>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(frequency_breakdown).map(([freq, count]) => (
                                    <span key={freq} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md capitalize">
                                        {freq}: {count}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {source_breakdown.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-900 mb-2">Entry source</h4>
                            <ul className="text-sm space-y-1">
                                {source_breakdown.map((row) => (
                                    <li key={row.source} className="flex justify-between text-gray-700">
                                        <span>{SOURCE_LABELS[row.source] || row.source}</span>
                                        <span className="font-semibold">{row.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg shadow p-5 lg:col-span-1">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">Top properties analysed</h4>
                    {top_properties.length === 0 ? (
                        <p className="text-sm text-gray-500">No events linked to a property yet.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {top_properties.map((p) => {
                                const displayAddress = p.full_address || p.address || `Property #${p.id}`;
                                return (
                                <li
                                    key={p.id}
                                    className="py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
                                    onClick={() => navigate(`/properties/${p.id}?tab=analytics`)}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                            {displayAddress}
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-sm font-semibold text-gray-900">{p.calculations} calcs</p>
                                        <p className="text-xs text-gray-500">
                                            {p.opens} opens · {p.broker_clicks || 0} broker
                                        </p>
                                    </div>
                                </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-5">
                <button
                    type="button"
                    onClick={() => setBuyersOpen((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    aria-expanded={buyersOpen}
                >
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        {buyersOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        All buyers — mortgage activity
                    </h4>
                    <p className="text-xs text-gray-500">
                        {buyer_summaries.length} signed-in buyer{buyer_summaries.length !== 1 ? 's' : ''}{buyersOpen ? '' : ' · Click to expand'}
                    </p>
                </button>
                {!buyersOpen ? null : buyer_summaries.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-3">No signed-in buyers have used the mortgage calculator in this period.</p>
                ) : (
                    <div className="overflow-x-auto mt-3">
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-2 pr-3">Buyer</th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Opens" column="opens" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Calcs" column="calculations" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Broker" column="broker_clicks" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pr-3">Last repayment</th>
                                    <th className="text-right py-2 pr-3">Borrowing power</th>
                                    <th className="text-right py-2 pr-3">
                                        <SortHeader label="Last active" column="last_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </th>
                                    <th className="text-right py-2 pl-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedBuyers.map((row) => {
                                    const calc = row.latest_calc || {};
                                    return (
                                        <tr
                                            key={row.buyer.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => openBuyer(row.buyer)}
                                        >
                                            <td className="py-2.5 pr-3">
                                                <p className="font-medium text-gray-900">{row.buyer.name}</p>
                                                <p className="text-xs text-gray-500">{row.buyer.email}</p>
                                            </td>
                                            <td className="py-2.5 pr-3 text-right">{row.opens}</td>
                                            <td className="py-2.5 pr-3 text-right">{row.calculations}</td>
                                            <td className="py-2.5 pr-3 text-right">{row.broker_clicks || 0}</td>
                                            <td className="py-2.5 pr-3 text-right">{formatCurrency(calc.repayment)}</td>
                                            <td className="py-2.5 pr-3 text-right">{formatCurrency(calc.borrowing_power)}</td>
                                            <td className="py-2.5 pr-3 text-right text-gray-500">{formatRelative(row.last_at)}</td>
                                            <td className="py-2.5 pl-2 text-right">
                                                <ExternalLink className="w-4 h-4 text-teal-500 inline-block" />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {drilldown && (
                <DrilldownModal
                    title={drilldown.title}
                    filter={drilldown.filter}
                    rows={drilldownRows}
                    loading={drilldownLoading}
                    onClose={() => setDrilldown(null)}
                    onBuyerClick={openBuyer}
                />
            )}
        </div>
    );
};

const DrilldownModal = ({ title, filter, rows, loading, onClose, onBuyerClick }) => {
    const isOpens = filter === 'opens';
    const isBroker = filter === 'broker';

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {loading ? 'Loading…' : `${rows.length} event${rows.length !== 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="p-5 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Loading activity…
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-sm text-gray-500">No activity in this window.</p>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                                <tr>
                                    <th className="text-left py-2 pr-3">User</th>
                                    {isOpens || isBroker ? (
                                        <th className="text-left py-2 pr-3">Property / source</th>
                                    ) : (
                                        <>
                                            <th className="text-right py-2 pr-3">Loan</th>
                                            <th className="text-right py-2 pr-3">Price</th>
                                            <th className="text-right py-2 pr-3">Repayment</th>
                                            <th className="text-right py-2 pr-3">LVR</th>
                                            <th className="text-left py-2 pr-3">Type</th>
                                        </>
                                    )}
                                    <th className="text-right py-2 pr-3">When</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((row) => {
                                    const propLabel = row.property
                                        ? (row.property.address || row.property.title || `Property #${row.property.id}`)
                                        : '—';
                                    const sourceLabel = SOURCE_LABELS[row.source] || row.source || '—';
                                    return (
                                        <tr
                                            key={row.event_id}
                                            className={row.buyer ? 'hover:bg-gray-50 cursor-pointer' : ''}
                                            onClick={() => row.buyer && onBuyerClick(row.buyer)}
                                        >
                                            <td className="py-2 pr-3">
                                                {row.buyer ? (
                                                    <div>
                                                        <p className="font-medium text-gray-900">{row.buyer.name}</p>
                                                        <p className="text-xs text-gray-500">{row.buyer.email}</p>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-500 italic">Anonymous</span>
                                                )}
                                            </td>
                                            {isOpens || isBroker ? (
                                                <td className="py-2 pr-3 text-gray-700 truncate max-w-xs">
                                                    {propLabel !== '—' ? propLabel : sourceLabel}
                                                </td>
                                            ) : (
                                                <>
                                                    <td className="py-2 pr-3 text-right">{formatCurrency(row.loan_amount)}</td>
                                                    <td className="py-2 pr-3 text-right">{formatCurrency(row.property_price)}</td>
                                                    <td className="py-2 pr-3 text-right">{formatCurrency(row.repayment)}</td>
                                                    <td className="py-2 pr-3 text-right">{formatPercent(row.lvr_pct)}</td>
                                                    <td className="py-2 pr-3 capitalize text-gray-700">
                                                        {row.loan_type || '—'}
                                                        {row.buyer_type ? ` · ${BUYER_TYPE_LABELS[row.buyer_type] || row.buyer_type}` : ''}
                                                    </td>
                                                </>
                                            )}
                                            <td className="py-2 pr-3 text-right text-gray-500">{formatRelative(row.occurred_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MortgageCalculatorAnalytics;
